<script setup lang="ts">
import {
  ref, computed, watch, watchEffect,
} from 'vue';
import { lastValueFrom } from 'rxjs';
import { getPlanPriceForUser, subscriptionBenefitPercentage } from '@kcalc/lib/browser';
import BaseButton from '@/components/BaseButton.vue';
import { upgradeSubscription$ } from '@/lib/firebase/functions';
import { useSystemMessage } from '@/composables/system-message';
import { useCheckout } from '@/composables/checkout';
import { useAuth } from '@/composables/firebase/auth';
import UpgradeIcon from '@/components/payment/UpgradeIcon.vue';
import { usePayment } from '@/composables/firebase/payment';
import { useGamification } from '@/composables/gamification';
import SubscriptionPeriodControl from '@/components/payment/SubscriptionPeriodControl.vue';

const emit = defineEmits(['close', 'isRunning']);

defineProps({
  feature: {
    type: String,
    required: false,
    default: null,
  },
});

const { setSystemMessageByCode, systemMessage } = useSystemMessage();
const { choosePlan } = useCheckout();
const {
  subscriptionPlans, fetchUpgradeInfo, upgradeInfo, subscription, subscriptionPeriod,
} = usePayment();
const { hasActiveSubscription, user } = useAuth();

const canDoDirectUpgrade = computed(() => hasActiveSubscription.value && user.value?.profile?.subscription?.status === 'active');

const loading = ref(true);

fetchUpgradeInfo().then(() => {
  loading.value = false;
});

const isUpgradeRunning = ref(false);

watch(isUpgradeRunning, (val) => {
  emit('isRunning', val);
});

watchEffect(() => {
  if (
    systemMessage.value?.i18nKey === 'systemMessages.success_subscription_upgrade'
    && isUpgradeRunning.value
  ) {
    isUpgradeRunning.value = false;
    emit('close');
  }
});

const upgrade = async () => {
  await lastValueFrom(upgradeSubscription$({ plan: 'business', period: subscriptionPeriod.value }))
    .then((response) => {
      if (response) {
        isUpgradeRunning.value = false;
        location.href = response;
      }
    })
    .catch((e) => {
      setSystemMessageByCode(e.message);
      isUpgradeRunning.value = false;
      emit('close');
    });
};

const handleUpgradeClick = async () => {
  isUpgradeRunning.value = true;

  if (canDoDirectUpgrade.value) {
    await upgrade();
  } else {
    await choosePlan('business', subscriptionPeriod.value, true);
  }
};

const newPlan = computed(() => subscriptionPlans.value?.find((p) => p.code === 'business'));

const upgradeFee = computed(() => upgradeInfo.value?.upgradeFeeNet.replace('.', ',').replace(',00', ''));
const hasToPayUpgradeFee = computed(() => subscription.value?.period === subscriptionPeriod.value);

const newPrice = computed(() => {
  if (newPlan.value && user.value?.profile) {
    return getPlanPriceForUser(newPlan.value, subscriptionPeriod.value, user.value.profile, true, false)
      .toFixed(2).replace('.', ',').replace(',00', '');
  }

  return undefined;
});

const yearlyPriceInfo = computed(() => {
  if (subscriptionPeriod.value === 'yearly' && newPlan.value && user.value?.profile) {
    const monthlyPrice = getPlanPriceForUser(newPlan.value, 'monthly', user.value.profile, true, false) * 12;
    const yearlyPrice = getPlanPriceForUser(newPlan.value, 'yearly', user.value.profile, true, false, true);

    return {
      monthlyPrice: monthlyPrice.toFixed(2).replace('.', ',').replace(',00', ''),
      yearlyPrice: yearlyPrice.toFixed(2).replace('.', ',').replace(',00', ''),
    };
  }

  return null;
});

const { redeemedLevel } = useGamification();
const benefit = computed(() => (
  redeemedLevel.value > 0
    ? subscriptionBenefitPercentage[redeemedLevel.value]
    : 0
));
const hasBenefit = computed(() => benefit.value > 0);
</script>

<template>
  <div class="business-upgrade-content">
    <UpgradeIcon with-background />

    <div class="headlines">
      <h1>{{ $t('upgrade.businessUpgradeModal.headline') }}</h1>

      <h2 v-if="feature">
        {{ $t('upgrade.businessUpgradeModal.missingFeatureHint', { feature }) }}
      </h2>
    </div>

    <p class="description">
      {{ $t('upgrade.businessUpgradeModal.description') }}
    </p>

    <ul
      class="advantages"
      v-html="$t('upgrade.businessUpgradeModal.advantages')" />

    <SubscriptionPeriodControl
      class="mb-2" />

    <div class="recommendation">
      <div>
        <font-awesome-icon :icon="['far', 'check-circle']" />
        {{ $t('upgrade.businessUpgradeModal.recommendation.label') }}
        <span class="recommended-badge">
          {{ $t('upgrade.businessUpgradeModal.recommendation.recommended') }}
        </span>
      </div>
      <div class="price">
        <span class="amount">{{ newPrice }} €</span> <span class="period">{{ $t('SubscriptionPlans.monthly') }}</span>

        <div
          v-if="yearlyPriceInfo"
          class="yearly-payment-info">
          <span>{{ yearlyPriceInfo.monthlyPrice }} €</span>
          <span class="ml-1">{{ yearlyPriceInfo.yearlyPrice }} €</span>
          {{ $t('SubscriptionPlans.perYear') }}
        </div>

        <div
          v-if="hasBenefit"
          class="benefit">
          ({{ $t('SubscriptionPlans.benefitInfo', { benefit, level: redeemedLevel }) }})
        </div>
      </div>
    </div>

    <div class="action">
      <BaseButton
        class="btn-primary"
        :disabled="loading || isUpgradeRunning"
        :loading="loading || isUpgradeRunning"
        @click="handleUpgradeClick">
        {{ $t('upgrade.businessUpgradeModal.cta') }}
      </BaseButton>
    </div>

    <p
      class="payment-hint"
      v-if="canDoDirectUpgrade && upgradeFee && hasToPayUpgradeFee">
      {{ $t('upgrade.businessUpgradeModal.paymentHint', { upgradeFee }) }}
    </p>
    <p class="vat-info">
      {{ $t('SubscriptionPlans.vatInfo') }}
    </p>
  </div>
</template>

<style>
.business-upgrade-content {
  text-align: center;

  .upgrade-icon {
    --size: 5rem;
    margin: var(--space-4) auto var(--space-4) auto;
  }

  .headlines {
    margin-bottom: var(--space-3);

    h2 {
      margin-top: var(--space-2);
      font-size: var(--font-size-default);
    }
  }

  .description {
    font-size: var(--font-size-default);
    line-height: var(--line-height-m);
  }

  .advantages {
    text-align: left;
    margin: var(--space-4) auto;
    max-width: 440px;
    font-size: var(--font-size-m);
    list-style: none;
    padding: 0;

    li {
      position: relative;
      padding: var(--space-2) 0 var(--space-2) var(--space-5);

      &:first-child {
        font-weight: var(--font-weight-bold);
      }

      &:before {
        content: "";
        width: 20px;
        height: 20px;
        display: block;
        background: url('@/assets/img/icons/check-circle-regular--secondary.png') no-repeat;
        background-size: contain;
        position: absolute;
        top: 6px;
        left: 0;
      }
    }
  }

  .recommendation {
    background: var(--color-primary-ultra-light);
    padding: var(--space-3);
    border-radius: var(--border-radius);
    max-width: 600px;
    margin: 0 auto;
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: var(--font-size-m);

    > div:first-child {
      display: flex;
      align-items: center;
    }

    .recommended-badge {
      background: var(--color-primary);
      padding: var(--space-1) var(--space-2);
      border-radius: var(--border-radius);
      font-size: var(--font-size-s);
      margin-left: var(--space-2);
    }

    .price {
      .amount {
        font-weight: var(--font-weight-bold);
        font-size: var(--font-size-xl);
      }

      .period {
        font-size: var(--font-size-xs);
      }

      .benefit {
        font-size: var(--font-size-xs);
        margin-top: calc(var(--space-1) / 2);
      }
    }

    .yearly-payment-info {
      margin-top: var(--space-1);
      font-size: var(--font-size-s);

      span {
        &:first-child {
          color: var(--color-danger);
          text-decoration: line-through;
        }
      }
    }

    svg {
      font-size: var(--font-size-xl);
      margin-right: var(--space-2);
    }
  }

  .action {
    margin: var(--space-5) 0 var(--space-2);
  }

  .payment-hint, .vat-info {
    font-size: var(--font-size-s);
    line-height: var(--line-height-m);
  }
}
</style>
